/**
 * Action Center completed/Activity API (DEV-1209). Powers the "Sent" / history
 * view with the caller's completed-or-originated items unioned server-side:
 * completed/skipped rhythm task instances, terminal workflow requests the
 * caller submitted, and KPI data points the caller entered.
 *
 * The shape is intentionally thin — identifiers, timestamps, outcome. Subject
 * localization and project/community name resolution stay on the frontend,
 * matching the inbox adapters' resolveName pattern.
 */

import { apiFetch } from "./client";

export interface CompletedActionItem {
  itemKey: string;
  kind: "rhythm" | "workflow" | "data";
  /** Workflow action_type, for the Type column mapping. */
  actionType?: string | null;
  subject: string;
  targetType?: string | null;
  targetGuid?: string | null;
  /** Workflow "other party" — recipient (caller submitted) or requester (caller approved). */
  counterpartyGuid?: string | null;
  /** Pre-resolved Source label (rhythm task → its community name). */
  sourceLabel?: string | null;
  /** Role label for rhythm tasks (slot display_title). */
  roleLabel?: string | null;
  /** True when the source slot is gone (role no longer held) — tray explains the blank Source/Role. */
  sourceRetired?: boolean;
  /** Lifecycle-thread key (assignment_guid) — invite + accept/decline/resign acks share it. */
  threadKey?: string | null;
  projectId?: string | null;
  /** schedule | finance | mission | kpi, when kind === "data". */
  dataSection?: string | null;
  /** completed | skipped | approved | rejected | rescinded | cancelled | entered */
  outcome: string;
  /** ISO-8601 completion timestamp. */
  completedAt: string;
  /** ISO-8601 due date when the item has one (rhythm tasks); null otherwise. */
  dueAt?: string | null;
  /** ISO-8601 "added" date — when the item entered the Action Center (rhythm:
   *  due − lead; workflow: created_at; null for data/grouped rows). */
  addedAt?: string | null;
}

/**
 * The caller's completed/originated items, newest first. Defaults server-side
 * to the trailing 90 days; pass ISO timestamps to widen or shift the window.
 * Capped at 500 rows.
 */
export async function fetchCompletedItems(opts?: {
  since?: string;
  until?: string;
}): Promise<CompletedActionItem[]> {
  const qs = new URLSearchParams();
  if (opts?.since) qs.set("since", opts.since);
  if (opts?.until) qs.set("until", opts.until);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<CompletedActionItem[]>(`/api/action-items/completed${suffix}`);
}
